import FuseUtils from '@fuse/utils/FuseUtils';
import { createSlice, EntityId, PayloadAction } from '@reduxjs/toolkit';
import i18n from 'i18n';
import { crmFieldData } from '../crm.constants';
import { ClientCrmField, CrmFieldStatusEnum, CrmFieldTypeEnum, CrmSettingsDTO, ValidationDTO } from '../types';
import CrmAdapter from './crm.adapter';
import { fetchCrmFieldValuesThunk, fetchCrmFormThunk, saveCrmFieldsThunk } from './crm.thunk';

export interface CrmState {
  isLoading: boolean;
  isEdited: boolean;
  hasError: boolean;
  selectedFieldId: EntityId | null;
  crmSettings: CrmSettingsDTO;
  crmNewFieldDialogOpened: boolean;
}

const initialState: CrmState = {
  isLoading: false,
  isEdited: false,
  hasError: false,
  selectedFieldId: null,
  crmSettings: {
    points: null,
    isRequired: false,
  },
  crmNewFieldDialogOpened: false,
};

export const { actions, reducer } = createSlice({
  name: 'crm',
  initialState: CrmAdapter.getInitialState(initialState),
  reducers: {
    setCrmFieldById: (state, action: PayloadAction<{ id: string; field: Partial<ClientCrmField> }>) => {
      state.isEdited = true;
      CrmAdapter.updateOne(state, {
        id: action.payload.id,
        changes: {
          ...action.payload.field,
        },
      });
    },

    setCrmFieldValidation: (state, action: PayloadAction<{ id: string; validation: Partial<ValidationDTO> }>) => {
      state.isEdited = true;
      const entity = state.entities[action.payload.id];
      if (entity?.validation) {
        entity.validation = {
          ...entity.validation,
          ...action.payload.validation,
        };
      }
    },

    setSelectedFieldId: (state, action: PayloadAction<EntityId>) => {
      state.selectedFieldId = action.payload;
    },

    openCrmNewFieldDialog: (state, action: PayloadAction<boolean>) => {
      state.crmNewFieldDialogOpened = action.payload;
    },

    addCrmField: (state, action: PayloadAction<CrmFieldTypeEnum>) => {
      const { isUserField } = crmFieldData[action.payload];
      const newField: Omit<ClientCrmField, 'id'> = {
        localId: FuseUtils.generateGUID(),
        label: isUserField
          ? i18n.t(`crm.${action.payload.toLowerCase()}.type`, { ns: 'crm' })
          : i18n.t('crm.newFieldLabel', { ns: 'crm' }),
        status: CrmFieldStatusEnum.ACTIVE,
        validation: {
          type: action.payload,
          isRequired: false,
        },
        isValid: true,
        isUserField,
        isLocked: false,
        value: null,
      };

      CrmAdapter.addOne(state, newField);
      state.selectedFieldId = newField.localId;
      state.isEdited = true;
    },
    deleteCrmField: (state, action: PayloadAction<EntityId>) => {
      CrmAdapter.removeOne(state, action.payload);
    },
    toggleFieldStatus: (state, action: PayloadAction<EntityId>) => {
      CrmAdapter.updateOne(state, {
        id: action.payload,
        changes: {
          status:
            state.entities[action.payload]?.status === CrmFieldStatusEnum.ARCHIVED
              ? CrmFieldStatusEnum.ACTIVE
              : CrmFieldStatusEnum.ARCHIVED,
        },
      });
      state.isEdited = true;
    },
    setCrmSettings: (state, action: PayloadAction<Partial<CrmSettingsDTO>>) => {
      state.crmSettings = {
        ...state.crmSettings,
        ...action.payload,
      };
      state.isEdited = true;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchCrmFormThunk.pending, state => {
      state.isLoading = true;
      state.hasError = false;
    });
    builder.addCase(fetchCrmFormThunk.fulfilled, (state, action) => {
      const oldFields = state.entities;
      CrmAdapter.setAll(state, action.payload.fields || []);
      // Presrve old fields values
      Object.keys(oldFields).forEach(key => {
        const oldField = oldFields[key];
        const newField = state.entities[key];
        if (newField && oldField && oldField.values) {
          newField.values = oldField.values;
        }
      });
      state.crmSettings = action.payload.settings;
      state.hasError = false;
      state.isLoading = false;
      state.isEdited = false;
    });
    builder.addCase(fetchCrmFormThunk.rejected, state => {
      CrmAdapter.setAll(state, []);
      state.hasError = true;
      state.isLoading = false;
    });
    builder.addCase(fetchCrmFieldValuesThunk.pending, state => {
      state.isLoading = true;
      state.hasError = false;
    });
    builder.addCase(fetchCrmFieldValuesThunk.fulfilled, (state, action) => {
      CrmAdapter.updateOne(state, {
        id: action.meta.arg,
        changes: {
          values: action.payload,
        },
      });
      state.isLoading = false;
      state.hasError = false;
    });
    builder.addCase(fetchCrmFieldValuesThunk.rejected, state => {
      state.isLoading = false;
      state.hasError = true;
    });
    builder.addCase(saveCrmFieldsThunk.pending, state => {
      state.isLoading = true;
      state.hasError = false;
    });
    builder.addCase(saveCrmFieldsThunk.fulfilled, (state, action) => {
      CrmAdapter.setAll(state, action.payload);
      state.isLoading = false;
      state.hasError = false;
      state.isEdited = false;
    });
    builder.addCase(saveCrmFieldsThunk.rejected, state => {
      state.isLoading = false;
    });
  },
});

export default reducer;
